import { init } from 'echarts';
import { ArrowPointerService } from './services/arrow-pointer.service';
import { TooltipService } from './services/tooltip-service';

export class Echarts {
    /**
     * Stores the chart instance.
     *
     * @var {EChartsType}
     */
    instance;

    /**
     * Stores the HTML element where the chart
     * will be drawn. This is different than the
     * ScalerChartBuilder element.
     *
     * @var {HTMLDivElement}
     */
    canvas;

    /**
     * Stores the HTML element where the canvas element will be appended.
     *
     * @var {HTMLDivElement}
     */
    element;

    /**
     * @var {TooltipService}
     */
    tooltipService;

    /**
     * @var {ArrowPointerService}
     */
    arrowPointerService;

    constructor() {
        this.tooltipService = new TooltipService();
        this.arrowPointerService = new ArrowPointerService();
    }

    /**
     * Creates the chart on the DOM.
     *
     * @param element The element where the chart will be rendered.
     * @param configuration The chart configuration.
     * @returns {void}
     */
    create(element, configuration, options) {
        this.options = options;
        if (this.instance !== undefined) return;
        this.canvas = document.createElement('div');
        this.canvas.style.height = '100%';
        this.canvas.style.width = '100%';
        element.append(this.canvas);
        this.element = element;
        const renderer = options?.renderer ?? 'canvas';
        this.instance = init(this.canvas, undefined, { renderer: renderer });
        this.instance.setOption(configuration);
        this.arrowPointerService.addArrowPointer(this.instance, element, configuration);
        this.tooltipService.applyCustomTooltip(this.instance, this.element);
        this.applyItemHighlightForOtherCharts(this.instance)
        this.applyMouseLeaveDownplayForOtherCharts(this.instance, element)
        this.applyGresbScoreChartCustomLogic(configuration, 1700);
        this.setActionsForRelatedCharts(options);
        window.addEventListener('closeAssetDetails', this.removeAssetSelection);
        this.addCustomLegendPerStack();
    }

    /**
     * Updates the chart on the DOM.
     *
     * @param configuration The new data from the server.
     * @returns {void}
     */
    update(configuration) {
        if (this.instance === undefined) return;
        if (configuration.last_update_started_at && this.instance?.getOption().last_update_started_at && new Date(configuration.last_update_started_at) < new Date(this.instance?.getOption().last_update_started_at)) return;
        this.instance?.setOption(configuration, configuration.notMergeOnUpdate ?? configuration.version == 2 ?? false);
        this.arrowPointerService.addArrowPointer(this.instance, this.element, configuration);
        this.tooltipService.applyCustomTooltip(this.instance, this.element);
        this.applyGresbScoreChartCustomLogic(configuration);
        this.addCustomLegendPerStack();
        this.applyItemHighlightForOtherCharts(this.instance);
    }

    /**
     * Deletes the chart from the screen.
     *
     * @returns {void}
     */
    delete() {
        if (this.instance === undefined) return;
        this.instance.dispose();
        this.canvas?.remove();
        this.instance = undefined;
        this.canvas = undefined;
    }

    /**
     * Determines if the chart is currently initiated.
     *
     * @returns {boolean} If the chart is initiated or not.
     */
    isInitiated() {
        return this.instance !== undefined;
    }

    applyItemHighlightForOtherCharts(chartInstance) {
        if (!chartInstance.getOption().series[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab) {
            return;
        }
        const self = this;
        setTimeout(() => {
            if (chartInstance.getOption().series[0]?.type == 'bar') {
                chartInstance.getZr().on('mousemove', function (params) {
                    const option = chartInstance.getOption();
                    const pointInPixel = [params.offsetX, params.offsetY];
                    const pointInGrid = chartInstance.convertFromPixel('grid', pointInPixel);
                    if (chartInstance.containPixel('grid', pointInPixel)) {
                        if (option.series[0]?.tooltip?.customTooltip?.dataCoverageChartId) {
                            document.getElementById(option.series[0]?.tooltip?.customTooltip?.dataCoverageChartId)?.classList.add('!z-20');
                        }
                        const barSeriesCount = option.series.filter(el => Boolean(el.data.length)).length - 2;
                        let totalForIndex = 0;
                        option.series.forEach((serie) => {
                            totalForIndex += parseFloat(serie.data[pointInGrid[0]]?.value ?? 0);
                        })
                        if (pointInGrid[1] > totalForIndex) {
                            chartInstance.dispatchAction({
                                type: 'showTip',
                                seriesIndex: barSeriesCount > 0 ? barSeriesCount : 0,
                                dataIndex: pointInGrid[0],
                            });
                        }
                        self.onMouseMoveHighlightOtherCharts(
                            chartInstance,
                            {
                                dataIndex: pointInGrid[0],
                                data: option.series[0].data[pointInGrid[0]]
                            }
                        );
                    }
                });
            } else {
                chartInstance.on('mousemove', function (params) {
                    if (chartInstance.getOption().series[0]?.tooltip?.customTooltip?.dataCoverageChartId) {
                        document.getElementById(chartInstance.getOption().series[0]?.tooltip?.customTooltip?.dataCoverageChartId)?.classList.add('!z-20');
                    }
                    self.onMouseMoveHighlightOtherCharts(chartInstance, params);
                })
            }
        }, 0);
        setTimeout(() => {
            if (chartInstance.getOption().series[0]?.type == 'bar') {
                // Remove the click event listener if it's already added.
                chartInstance.getZr().off('click')
                chartInstance.getZr().on('click', function (params) {
                    const option = chartInstance.getOption();
                    const pointInPixel = [params.offsetX, params.offsetY];
                    const pointInGrid = chartInstance.convertFromPixel('grid', pointInPixel);
                    if (chartInstance.containPixel('grid', pointInPixel)) {
                        self.onClickOpenAssetDetails(
                            chartInstance,
                            {
                                dataIndex: pointInGrid[0],
                                data: option.series[0].data[pointInGrid[0]]
                            }
                        );
                    }
                });
            }
        }, 1000);
        let chartConfig = chartInstance.getOption();
        if (chartConfig.selectedAssetId) {
            const self = this;
            chartConfig.series[0]['data'].forEach(function (item, index) {
                if (item?.tooltipData?.assetData?.id == chartConfig.selectedAssetId) {
                    self.selectAssetInChart(chartInstance, item);
                }
            });
        }

    }

    removeItemHighlightForOtherCharts(chartInstance) {
        if (!chartInstance?.getOption()?.series[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab) {
            return;
        }
        setTimeout(() => {
            chartInstance.dispatchAction({
                type: 'downplay',
            });
        }, 50);
    }

    onMouseMoveHighlightOtherCharts(chartInstance, params) {
        let configuration = chartInstance.getOption();
        let hasIsClickedItem = false;
        let chartTab = chartInstance.getOption()?.series[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab;
        Object.values(ScalerChartBuilder.charts).forEach(
            function(chart) {
                let chartConfig = chart.provider.instance?.getOption();
                if (chartTab == chartConfig?.series[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab) {
                    if (chartConfig.series[0]['data'].length) {
                        let highlightIndex = null;
                        chartConfig.series[0]['data'].forEach(function (item, index) {
                            if (chartConfig.series[0]['data'][index]?.itemStyle?.clicked) {
                                hasIsClickedItem = true;
                            }
                            if (!hasIsClickedItem && params.data?.tooltipData?.assetData?.id && item?.tooltipData?.assetData?.id === params.data.tooltipData.assetData.id) {
                                highlightIndex = index;
                            }
                        })
                        if (!hasIsClickedItem && highlightIndex !== null) {
                            chart.provider.instance.dispatchAction({
                                type: 'downplay',
                            });
                            chart.provider.instance.dispatchAction({
                                type: 'highlight',
                                dataIndex: highlightIndex,
                            });
                        }
                    }
                }
            }
        );
        configuration.series[0]['data'].forEach(function (item, index) {
            if (configuration.series[0]['data'][index]?.itemStyle?.clicked) {
                hasIsClickedItem = true;
                return;
            }
        });
        chartInstance.dispatchAction({
            type: 'downplay',
        });
        if (!hasIsClickedItem) {
            chartInstance.dispatchAction({
                type: 'highlight',
                dataIndex: params.dataIndex,
            });
        }
    }

    onClickOpenAssetDetails(chartInstance, params) {
        const chartSeries = chartInstance?.getOption()?.series?.[0];
        if (!chartSeries) {
            // No series found, which is unlikely.
            return;
        }

        const assetId = chartInstance?.getOption()?.series?.[0]?.data[params.dataIndex]?.tooltipData?.assetData?.id;
        if (!assetId) {
            // No asset ID, probably just a chart that does not show asset data, such as a yearly totals chart.
            return;
        }

        window.dispatchEvent(new CustomEvent("showAssetDetails", {
            detail: {
                chartName: this.options?.name,
                scope: this.options?.scope,
                assetId: assetId
            }
        }));

        this.selectAssetInChart(chartInstance, params.data);
        let chartTab = chartSeries.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab;
        document.getElementsByClassName(`js-asset-details-loader-${chartTab}`)[0]?.classList?.remove('hidden')
    }

    selectAssetInChart(chartInstance, itemValue) {
        const chartTab = chartInstance?.getOption().series?.[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab;
        if (!chartTab) {
            return;
        }
        Object.values(ScalerChartBuilder.charts).forEach(
            function(chart) {
                const chartConfig = chart?.provider?.instance?.getOption();
                if (!chartConfig) {
                    return;
                }
                // TODO Update this.
                let baseColor = [
                    '#435D75',
                    '#64B5FF',
                    '#BDDFFF'
                ];
                if (chartConfig.preventOpacityUpdateOnAssetSelect) {
                    return;
                }
                if (chartTab == chartConfig.series[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab && chartConfig.series[0]['data'].length) {
                    setTimeout(() => {
                        chart.provider.instance.dispatchAction({
                            type: 'downplay',
                        });
                    }, 50);
                    chartConfig.series.forEach(function (series, seriesIndex) {
                        series.data.forEach(function (item, index) {
                            if (!chartConfig.series[seriesIndex]['data'][index].itemStyle?.hidden) {
                                if (item?.tooltipData?.assetData?.id === itemValue.tooltipData.assetData.id) {
                                    chartConfig.series[seriesIndex]['data'][index].itemStyle.clicked = true;
                                    if (!chartConfig.preventColorUpdateOnAssetSelect) {
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.color = chartConfig.series[seriesIndex]?.tooltip?.customTooltip?.availableHighlightColors[seriesIndex] ?? chartConfig.series[seriesIndex]?.tooltip?.customTooltip?.highlightColor;
                                    }
                                    chartConfig.series[seriesIndex]['data'][index].itemStyle.opacity = 1;
                                    if (chartConfig.series[seriesIndex].blur?.itemStyle?.opacity) {
                                        chartConfig.series[seriesIndex].blur.itemStyle.opacity = chartConfig.series[seriesIndex]?.tooltip?.customTooltip?.blurOpacity ?? 0.65;
                                    }
                                    if (chartConfig.series[seriesIndex]['data'][index].itemStyle?.decal?.color) {
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.borderColor = chartConfig.series[seriesIndex]['data'][index].itemStyle.color;
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.decal.color = chartConfig.series[seriesIndex]['data'][index].itemStyle.color;
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.color = 'white';
                                    }
                                } else if(chartConfig.series[seriesIndex]['data'][index]?.itemStyle) {
                                    chartConfig.series[seriesIndex]['data'][index].itemStyle.clicked = false;
                                    const blurColorFromSeries = chartConfig.series[seriesIndex].blur?.itemStyle?.color;
                                    if (!chartConfig.preventColorUpdateOnAssetSelect) {
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.color =  blurColorFromSeries ?? (chartConfig.series.length > 2 ? baseColor[seriesIndex] ?? baseColor[2] : baseColor[2]);
                                    }
                                    if (chartConfig.series[seriesIndex]['data'][index].itemStyle?.decal?.color) {
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.borderColor = chartConfig.series[seriesIndex]['data'][index].itemStyle.color;
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.decal.color = chartConfig.series[seriesIndex]['data'][index].itemStyle.color;
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.color = 'white';
                                    }
                                    chartConfig.series[seriesIndex]['data'][index].itemStyle.opacity = 0.25;
                                    if (chartConfig.series[seriesIndex].blur) {
                                        if (chartConfig.blurColor && chartConfig.blurColor[seriesIndex]) {
                                            chartConfig.series[seriesIndex].blur.itemStyle.color = chartConfig.blurColor[seriesIndex];
                                        } else {
                                            chartConfig.series[seriesIndex].blur.itemStyle.color = blurColorFromSeries ?? chartConfig.series[seriesIndex]?.tooltip?.customTooltip?.baseColor;
                                        }
                                        chartConfig.series[seriesIndex].blur.itemStyle.opacity = 0.25;
                                    }
                                }
                            }
                        })
                        if (chartConfig.series[seriesIndex].areaStyle) {
                            chartConfig.series[seriesIndex].areaStyle.opacity = 0.25;
                        }
                    })
                } else if (chartTab == chart.options?.options?.scope) {
                    chartConfig.series.forEach(function (series, seriesIndex) {
                        if (series.type !== 'gauge') {
                            if (!chartConfig.preventColorUpdateOnAssetSelect) {
                                chartConfig.series[seriesIndex].itemStyle = { color: chartConfig.series.length > 1 ? (baseColor[seriesIndex] ?? baseColor[2]) : baseColor[2], opacity: 0.25 };
                            }
                            if (series.type == 'line') {
                                chartConfig.series[seriesIndex].lineStyle = { opacity: 0.25 };
                            }
                            series.data.forEach(function (item, index) {
                                if (chartConfig.series[seriesIndex]['data'][index]?.itemStyle) {
                                    if (!chartConfig.preventColorUpdateOnAssetSelect) {
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.color = chartConfig.series.length > 1 ? baseColor[seriesIndex] ?? baseColor[2] : baseColor[2];
                                    }
                                    chartConfig.series[seriesIndex]['data'][index].itemStyle.opacity = 0.25;
                                }
                            })
                            if (chartConfig.series[seriesIndex].areaStyle) {
                                chartConfig.series[seriesIndex].areaStyle.opacity = 0.25;
                            }
                        }
                    });
                }
                chart.provider.instance.setOption({ series: chartConfig.series })
            }
        );
    }

    applyMouseLeaveDownplayForOtherCharts(chartInstance, element) {
        const customTooltip = chartInstance?.getOption()?.series?.[0]?.tooltip?.customTooltip;
        if (!customTooltip) {
            return;
        }
        const highlightAssetId = customTooltip.highlightAssetInAllChartsOnTab;
        if (!highlightAssetId) {
            return;
        }

        const chartElement = element.querySelector('svg') ?? element.querySelector('canvas');

        chartElement.addEventListener('mouseleave', function() {
            Object.values(ScalerChartBuilder.charts).forEach(
                function(otherChart) {
                    const otherChartInstance = otherChart?.provider?.instance;
                    if (!otherChartInstance) {
                        return;
                    }

                    const otherChartSeries = otherChartInstance.getOption()?.series?.[0];
                    if (!otherChartSeries) {
                        return;
                    }
                    if (highlightAssetId !== otherChartSeries.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab) {
                        return;
                    }

                    setTimeout(() => {
                        let isClicked = false;
                        otherChartSeries.data?.forEach(function (item) {
                            if (item.itemStyle?.clicked) {
                                isClicked = true;
                            }
                        });
                        if (!isClicked) {
                            otherChartInstance.dispatchAction({
                                type: 'downplay',
                            });
                        }
                    }, 100);
                }
            );
            if (customTooltip.dataCoverageChartId) {
                document.getElementById(customTooltip.dataCoverageChartId)?.classList.remove('!z-20');
            }
        });
    }

    applyGresbScoreChartCustomLogic(configuration, timeout) {
        if (configuration.customGresbChart && typeof applyGresbScoreChartCustomLogic === 'function') {
            if (timeout) {
                setTimeout(() => {
                    applyGresbScoreChartCustomLogic();
                }, timeout);
                return;
            }
            applyGresbScoreChartCustomLogic();
        }
    }

    /**
     * This adds actions for related charts, e.g. selecting legend in histogram charts should also select legend in
     * timeline chart that is next to it.
     */
    setActionsForRelatedCharts(options) {
        if (options?.related_charts) {
            options?.related_charts.forEach(item => {
                this.instance.on('legendselectchanged', (selected) => {
                    if (ScalerChartBuilder.charts[item]) {
                        ScalerChartBuilder.charts[item].provider.instance.dispatchAction({
                            type: selected.selected[selected.name] ? 'legendSelect' : 'legendUnSelect',
                            name: selected.name
                        });
                        ScalerChartBuilder.charts[item].provider.instance.dispatchAction({
                            type: selected.selected[selected.name + ' data collection pending'] ? 'legendSelect' : 'legendUnSelect',
                            name: selected.name + ' data collection pending'
                        });
                    }
                })
            });
        }
    }

    addCustomLegendPerStack() {
        const options = this.instance.getOption();
        if (options.legend && options.legend[0] && options.legend[0].showCustomLegendPerStack) {
            this.instance.setOption({
                legend: {
                    ...options.legend,
                    formatter:  function (name) {
                        const nameParts = name.split('|');
                        return nameParts[0] + (nameParts[2] && nameParts[2].includes('estimated') ? ', estimated' : '');
                    }
                }
            });
            const legendContainer = document.createElement('div');
            legendContainer.innerHTML = ``;
            let content = '';
            const id = this.element.id;
            const stacks = {};
            this.instance.getOption().series.forEach(serie => {
                if (serie.stack) {
                    serie.isEstimated = serie.name.includes('estimated');
                    stacks[serie.isEstimated ? serie.stack + ', estimated' : serie.stack] = serie;
                }
            });
            Object.entries(stacks).forEach(stack => {
                const stackDisplayName = stack[0];
                const serieObject = stack[1];
                if (serieObject.isEstimated) {
                    const decalColor = serieObject.itemStyle.decal.color;
                    content += `<div class="flex items-center hover:opacity-75 text-sm" id="${id}-${stackDisplayName}"><div class="w-4 h-4 rounded mr-2 cursor-pointer" style="border: solid 1px; border-color: ${decalColor};">
                    <svg width="15px" height="15px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 5C7 6.10457 6.10457 7 5 7C3.89543 7 3 6.10457 3 5C3 3.89543 3.89543 3 5 3C6.10457 3 7 3.89543 7 5Z" fill="${decalColor}"/>
                        <path d="M14 5C14 6.10457 13.1046 7 12 7C10.8954 7 10 6.10457 10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5Z" fill="${decalColor}"/>
                        <path d="M19 7C20.1046 7 21 6.10457 21 5C21 3.89543 20.1046 3 19 3C17.8954 3 17 3.89543 17 5C17 6.10457 17.8954 7 19 7Z" fill="${decalColor}"/>
                        <path d="M7 12C7 13.1046 6.10457 14 5 14C3.89543 14 3 13.1046 3 12C3 10.8954 3.89543 10 5 10C6.10457 10 7 10.8954 7 12Z" fill="${decalColor}"/>
                        <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="${decalColor}"/>
                        <path d="M21 12C21 13.1046 20.1046 14 19 14C17.8954 14 17 13.1046 17 12C17 10.8954 17.8954 10 19 10C20.1046 10 21 10.8954 21 12Z" fill="${decalColor}"/>
                        <path d="M5 21C6.10457 21 7 20.1046 7 19C7 17.8954 6.10457 17 5 17C3.89543 17 3 17.8954 3 19C3 20.1046 3.89543 21 5 21Z" fill="${decalColor}"/>
                        <path d="M14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19Z" fill="${decalColor}"/>
                        <path d="M19 21C20.1046 21 21 20.1046 21 19C21 17.8954 20.1046 17 19 17C17.8954 17 17 17.8954 17 19C17 20.1046 17.8954 21 19 21Z" fill="${decalColor}"/>
                    </svg>
                    </div>${stackDisplayName}</div>`;
                } else {
                    content += `<div class="flex items-center hover:opacity-75 text-sm" id="${id}-${stackDisplayName}"><div class="w-4 h-4 rounded mr-2 cursor-pointer" style="background: ${serieObject.itemStyle.color};"></div>${stackDisplayName}</div>`;
                }
            })
            legendContainer.innerHTML = content;
            legendContainer.classList.add('inline-flex', 'w-full', 'justify-end', 'mb-6', 'items-center', 'gap-x-4', 'js-custom-legend-per-stack');
            if (this.element.getElementsByClassName('js-custom-legend-per-stack')[0]) {
                this.element.removeChild(this.element.getElementsByClassName('js-custom-legend-per-stack')[0])
            }
            this.element.prepend(legendContainer);
            Object.entries(stacks).forEach(stack => {
                const stackSeriesObject = stack[1];
                const el = document.getElementById(`${id}-${stackSeriesObject.stack}${stackSeriesObject.isEstimated ? ', estimated' : ''}`);
                el?.addEventListener('click', () => {
                    this.instance.getOption().series.forEach(serie => {
                        const isSeriesEstimated = serie.name.includes('estimated');
                        if (serie.stack == stackSeriesObject.stack && !stackSeriesObject.isEstimated && !isSeriesEstimated || serie.stack == stackSeriesObject.stack && stackSeriesObject.isEstimated && isSeriesEstimated) {
                            this.instance.dispatchAction({
                                type: el.classList.contains('inactive-tab-item-filter') ? 'legendSelect' : 'legendUnSelect',
                                name: serie.name
                            });
                        }
                    })
                    if (el.classList.contains('inactive-tab-item-filter')) {
                        el.classList.remove('inactive-tab-item-filter');
                    } else {
                        el.classList.add('inactive-tab-item-filter');
                    }
                })
            })
        }
    }

    removeAssetSelection(e) {
        Object.values(ScalerChartBuilder.charts).forEach((chart) => {
            let chartConfig = chart?.provider?.instance?.getOption();
            if (!chartConfig) {
                return;
            }
            if (chart.options.options.scope !== e.detail.scope) {
                return;
            }
            chart.provider.instance.dispatchAction({
                type: 'downplay',
            });
            if (chartConfig.preventOpacityUpdateOnAssetSelect) {
                return;
            }
            if (chartConfig.series[0]?.tooltip?.customTooltip?.highlightAssetInAllChartsOnTab && chartConfig.series[0]['data'].length) {
                chartConfig.series.forEach(function (series, seriesIndex) {
                    series.data.forEach(function (item, index) {
                        if (!chartConfig.series[seriesIndex]['data'][index].itemStyle?.hidden) {
                            if(chartConfig.series[seriesIndex]['data'][index]?.itemStyle) {
                                chartConfig.series[seriesIndex]['data'][index].itemStyle.clicked = false;
                                if (!chartConfig.preventColorUpdateOnAssetSelect) {
                                    chartConfig.series[seriesIndex]['data'][index].itemStyle.color = chartConfig.color[seriesIndex];
                                    if (chartConfig.series[seriesIndex]['data'][index].itemStyle?.decal?.color) {
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.borderColor = chartConfig.series[seriesIndex]['data'][index].itemStyle.color;
                                        chartConfig.series[seriesIndex]['data'][index].itemStyle.decal.color = chartConfig.series[seriesIndex]['data'][index].itemStyle.color;
                                    }
                                }
                                chartConfig.series[seriesIndex]['data'][index].itemStyle.opacity = 1;
                            }
                        }
                    })
                    if (chartConfig.series[seriesIndex].blur) {
                        chartConfig.series[seriesIndex].blur.itemStyle.opacity = chartConfig.series[0]?.tooltip?.customTooltip?.blurOpacity ?? 0.65;
                        if (chartConfig.series[seriesIndex]['data'][0]?.itemStyle?.decal?.color) {
                            chartConfig.series[seriesIndex].blur.itemStyle.color = 'white';
                        }
                    }
                })
            } else {
                chartConfig.series.forEach(function (series, seriesIndex) {
                    if (chartConfig.series[seriesIndex].areaStyle) {
                        chartConfig.series[seriesIndex].areaStyle.opacity = chartConfig.series[seriesIndex].areaStyle.defaultOpacity ?? 0.6;
                    }
                    if (!chartConfig.preventColorUpdateOnAssetSelect) {
                        chartConfig.series[seriesIndex].itemStyle = { color: chartConfig.color[seriesIndex], opacity: 1 };
                    }
                    if (series.type == 'line') {
                        chartConfig.series[seriesIndex].lineStyle = { opacity: 1 };
                    }
                    series.data.forEach(function (item, index) {
                        if (chartConfig.series[seriesIndex]['data'][index]?.itemStyle) {
                            if (!chartConfig.preventColorUpdateOnAssetSelect) {
                                chartConfig.series[seriesIndex]['data'][index].itemStyle.color = chartConfig.color[seriesIndex];
                            }
                            chartConfig.series[seriesIndex]['data'][index].itemStyle.opacity = 1;
                        }
                    })
                });
            }
            chart.provider.instance.setOption({ series: chartConfig.series })
        })
    }
}
